import React, { lazy, Suspense, useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from "react-router-dom";
import { Box, CircularProgress } from '@mui/material';
import { checkUserStatus } from '../api/auth.api';

// Layout Components
import Outletcomponent from '../components/Outletcomponent';
import OutletUnAuthcomponent from '../components/OutletUnAuthcomponent';

// Lazy Loaded Components
const Home = lazy(() => import('./home/Home'));
const Aboutus = lazy(() => import('./about/Aboutus'));
const Contactus = lazy(() => import('./contact/Contactus'));
const Wishlist = lazy(() => import('./wishlist/Wishlist'));
const Cart = lazy(() => import('./cart/Cart'));
const Login = lazy(() => import('./login/Login'));
const Register = lazy(() => import('./register/Register'));
const Catalogues = lazy(() => import('./catalogues/Catalogues'));
const Myorder = lazy(() => import('./myoder/Myorder'));
const OrderId = lazy(() => import('./orderid/OrderId'));
const MyQuotations = lazy(() => import('./quotations/MyQuotations'));
const QuotationID = lazy(() => import('./quotationid/QuotationID'));
const ShopCategory = lazy(() => import('./shop/ShopCategory'));
const ShopSubCategory = lazy(() => import('./shop/ShopSubCategory'));
const Checkout = lazy(() => import('./checkout/Checkout'));
const ProductDetailPage = lazy(() => import('./productdetailspage/ProductDetailPage'));
const WelcomeScreen = lazy(() => import('./welcomescreen/WelcomeScreen'));
const SearchResults = lazy(() => import('./search/SearchResults'));
const Profile = lazy(() => import('./profile/Profile'));
const Chat = lazy(() => import('./chat/Chat'));
const Test = lazy(() => import('./test'));
const RegisterOtp = lazy(() => import('./register/RegisterOtp'));
const TermsAndConditions = lazy(() => import('./terms/TermsAndConditions'));
const PrivacyPolicy = lazy(() => import('./privacy/PrivacyPolicy'));
const DeliveryAndReturn = lazy(() => import('./delivery/DeliveryAndReturn'));




const PageLoader = () => (
  <Box sx={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '100vh',
    width: '100%',
    bgcolor: '#f8f9fa'
  }}>
    <CircularProgress sx={{ color: '#695337' }} />
  </Box>
);

const ProtectedRoute = ({ children, isAuthenticated, isApproved, requireApproval }) => {
  const location = useLocation();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requireApproval && !isApproved) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const PageRoutes = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return localStorage.getItem('token') ? true : false;
  });
  
  const [isApproved, setIsApproved] = useState(() => {
    const token = localStorage.getItem('token');
    if (!token) return true;
    return localStorage.getItem('isApproved') === 'true';
  });

  useEffect(() => {
    const checkAuth = async () => {
      if (isAuthenticated) {
        const mobno = localStorage.getItem('loginname');
        const token = localStorage.getItem('token');
        const deviceId = localStorage.getItem('device_id');
        
        if (mobno) {
          try {
            const response = await checkUserStatus();
            
            if (response && response.Status === 1 && Array.isArray(response.Data) && response.Data.length > 0) {
              const statusVal = response.Data[0]?.status;
              const isUserApproved = statusVal === "True" || statusVal === true;
              setIsApproved(isUserApproved);
              localStorage.setItem('isApproved', isUserApproved ? 'true' : 'false');
            }
            
            // Check if the response indicates an expired/invalid token
            if (response && response.Status === "0" && response.message === "Unauthorised Access") {
              console.log("Token expired on initial load, attempting to refresh...");
              
              const refreshResponse = await fetch('https://api.jdecor.in/API/AUTH', {
                method: 'POST',
                headers: {
                  'loginname': mobno,
                  'token': token,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  "grant_type": "refresh_token",
                  "device_id": deviceId
                })
              });
              
              const refreshData = await refreshResponse.json();
              
              if (refreshData && refreshData.Status === 1 && refreshData.token) {
                console.log("Token refreshed successfully.");
                localStorage.setItem('token', refreshData.token);
                
                // Retry check status
                const retryResponse = await checkUserStatus();
                if (retryResponse && retryResponse.Status === 1 && Array.isArray(retryResponse.Data) && retryResponse.Data.length > 0) {
                  const statusVal = retryResponse.Data[0]?.status;
                  const isUserApproved = statusVal === "True" || statusVal === true;
                  setIsApproved(isUserApproved);
                  localStorage.setItem('isApproved', isUserApproved ? 'true' : 'false');
                }
              } else {
                console.error("Failed to refresh token.");
                setIsAuthenticated(false);
                localStorage.clear();
              }
            }
          } catch (error) {
            console.error("Failed to check status on initial load:", error);
          }
        }
      }
    };
    checkAuth();
  }, [isAuthenticated]); // Run only on initial mount/refresh and auth state changes
  
  useEffect(() => {
    let intervalId;

    if (isAuthenticated && !isApproved) {
      intervalId = setInterval(async () => {
        try {
          const response = await checkUserStatus();
          if (response && response.Status === 1 && Array.isArray(response.Data) && response.Data.length > 0) {
            const statusVal = response.Data[0]?.status;
            const isUserApproved = statusVal === "True" || statusVal === true;
            if (isUserApproved) {
              setIsApproved(true);
              localStorage.setItem('isApproved', 'true');
            }
          }
        } catch (error) {
          console.error("Failed to check status in polling:", error);
        }
      }, 30000); // Check every 30 seconds
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isAuthenticated, isApproved]);

  return (
    <Router>
      <ScrollToTop />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public Routes (No Layout or UnAuth Layout) */}
          <Route element={<OutletUnAuthcomponent />}>
            <Route path='/welcomescreen' element={<WelcomeScreen />} />
            <Route path="/login" element={<Login setIsAuthenticated={setIsAuthenticated} />} />
            <Route path="/register" element={<Register setIsAuthenticated={setIsAuthenticated} />} />
            <Route path="/register-otp" element={<RegisterOtp setIsAuthenticated={setIsAuthenticated} />} />
          </Route>




          {/* Main App Routes (Requires Outletcomponent) */}
          <Route element={<Outletcomponent isAuthenticated={isAuthenticated} setIsAuthenticated={setIsAuthenticated} isApproved={isApproved} />}>

            {/* Routes accessible to everyone */}
            <Route path="/" element={<Home isAuthenticated={isAuthenticated} isApproved={isApproved} />} />
            <Route path="/Home" element={<Home isAuthenticated={isAuthenticated} isApproved={isApproved} />} />
            <Route path="/About" element={<Aboutus />} />
            <Route path="/Contactus" element={<Contactus />} />
            <Route path="/terms" element={<TermsAndConditions />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/delivery-returns" element={<DeliveryAndReturn />} />
            <Route 
              path="/shop" 
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated} isApproved={isApproved} requireApproval={true}>
                  <ShopCategory />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/shop/:categoryId" 
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated} isApproved={isApproved} requireApproval={true}>
                  <ShopCategory />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/shop/:categoryId/:subCategoryId" 
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated} isApproved={isApproved} requireApproval={true}>
                  <ShopSubCategory />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/productdetail/:prdCode" 
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated} isApproved={isApproved} requireApproval={true}>
                  <ProductDetailPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/search" 
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated} isApproved={isApproved} requireApproval={true}>
                  <SearchResults />
                </ProtectedRoute>
              } 
            />
            <Route path="/catalogues" element={<Catalogues />} />

            {/* Routes requiring authentication */}
            <Route 
              path="/profile" 
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated} isApproved={isApproved} requireApproval={true}>
                  <Profile />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/chat" 
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated} isApproved={isApproved} requireApproval={true}>
                  <Chat />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/Cart" 
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated} isApproved={isApproved} requireApproval={true}>
                  <Cart />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/Wishlist" 
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated} isApproved={isApproved} requireApproval={true}>
                  <Wishlist />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/myorder" 
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated} isApproved={isApproved} requireApproval={true}>
                  <Myorder />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/orderid" 
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated} isApproved={isApproved} requireApproval={true}>
                  <OrderId />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/myquotations" 
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated} isApproved={isApproved} requireApproval={true}>
                  <MyQuotations />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/quotationid/:quotekey" 
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated} isApproved={isApproved} requireApproval={true}>
                  <QuotationID />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/checkout" 
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated} isApproved={isApproved} requireApproval={true}>
                  <Checkout />
                </ProtectedRoute>
              } 
            />
          </Route>

          {/* Utils/Testing */}
          <Route path='/test-api' element={<Test />} />

          {/* 404 Not Found - Redirect to home or custom page */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </Router>
  );
};

export default PageRoutes;